import React, { useEffect, useMemo, useState } from "react";
import { Button, Form, Input, Select, Space, Switch, Table, Tag } from "antd";
import {
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import {
  DiscountFormModal,
  modeOptions,
  orderTypeOptions,
} from "./DiscountModals";
import axios from "utils/axios";
import configFile from "config.json";
import { NotificationManager } from "react-notifications";

const DISCOUNT_API_BASE = `${configFile.PROD_SERVER_URL}/menu2/discount`;

const modeLabel = (value) =>
  modeOptions.find((opt) => opt.value === value)?.label || value;

const orderTypeLabel = (value) =>
  orderTypeOptions.find((opt) => opt.value === value)?.label || value;

const Discount = () => {
  const [discounts, setDiscounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [form] = Form.useForm();
  const [filters, setFilters] = useState({
    search: "",
    orderType: "all",
    status: "all",
  });

  useEffect(() => {
    fetchDiscounts();
  }, []);

  const fetchDiscounts = async () => {
    setIsLoading(true);
    try {
      const result = await axios.post(`${DISCOUNT_API_BASE}/list`);
      const rows = result?.data?.data || result?.data || [];
      setDiscounts(Array.isArray(rows) ? rows : []);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredDiscounts = useMemo(() => {
    const search = filters.search.toLowerCase();
    return discounts.filter((discount) => {
      const matchSearch =
        (discount.code || "").toLowerCase().includes(search) ||
        (discount.name || "").toLowerCase().includes(search);
      const matchOrderType =
        filters.orderType === "all" || discount.orderType === filters.orderType;
      const matchStatus =
        filters.status === "all" ||
        (filters.status === "active" ? discount.isActive : !discount.isActive);
      return matchSearch && matchOrderType && matchStatus;
    });
  }, [filters, discounts]);

  const handleOpenCreate = () => {
    setEditingDiscount(null);
    form.resetFields();
    form.setFieldsValue({ mode: "percentage", orderType: "both", minOrderAmount: 0 });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (discount) => {
    setEditingDiscount(discount);
    form.setFieldsValue({
      code: discount.code,
      name: discount.name,
      objectNum: discount.objectNum,
      mode: discount.mode,
      value: Number(discount.value),
      minOrderAmount: Number(discount.minOrderAmount),
      orderType: discount.orderType,
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      let response;
      if (editingDiscount) {
        response = await axios.post(`${DISCOUNT_API_BASE}/update`, {
          id: editingDiscount.id,
          ...values,
        });
      } else {
        response = await axios.post(`${DISCOUNT_API_BASE}/create`, values);
      }
      if (response?.data?.success) {
        NotificationManager.success("Амжилттай хадгаллаа");
        fetchDiscounts();
      }
      setIsModalOpen(false);
      form.resetFields();
      setEditingDiscount(null);
    } catch (error) {
      if (error?.errorFields) return; // form validation error, antd already shows it
      console.log(error);
      NotificationManager.error(
        error?.response?.data?.errorMessage || "Алдаа гарлаа"
      );
    }
  };

  const handleToggle = async (discount) => {
    try {
      const response = await axios.post(`${DISCOUNT_API_BASE}/toggle`, {
        id: discount.id,
      });
      if (response?.data?.success) {
        NotificationManager.success("Төлөв шинэчлэгдлээ");
        fetchDiscounts();
      } else {
        NotificationManager.error(
          response?.data?.errorMessage || "Алдаа гарлаа"
        );
      }
    } catch (error) {
      console.log(error);
      NotificationManager.error(
        error?.response?.data?.errorMessage || "Алдаа гарлаа"
      );
    }
  };

  const columns = [
    {
      title: "Код",
      dataIndex: "code",
      render: (value) => <Tag color="blue">{value}</Tag>,
    },
    {
      title: "Нэр",
      dataIndex: "name",
    },
    {
      title: "Object №",
      dataIndex: "objectNum",
    },
    {
      title: "Төрөл",
      dataIndex: "mode",
      render: (value) => modeLabel(value),
    },
    {
      title: "Утга",
      dataIndex: "value",
      render: (value, record) =>
        record.mode === "percentage"
          ? `${Number(value)}%`
          : `${Number(value).toLocaleString()}₮`,
    },
    {
      title: "Доод дүн",
      dataIndex: "minOrderAmount",
      render: (value) => `${Number(value || 0).toLocaleString()}₮`,
    },
    {
      title: "Захиалгын төрөл",
      dataIndex: "orderType",
      render: (value) => <Tag>{orderTypeLabel(value)}</Tag>,
    },
    {
      title: "Төлөв",
      dataIndex: "isActive",
      render: (value, record) => (
        <Switch
          checked={value}
          checkedChildren="Идэвхтэй"
          unCheckedChildren="Идэвхгүй"
          onChange={() => handleToggle(record)}
        />
      ),
    },
    {
      title: "Үйлдэл",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => handleOpenEdit(record)}>
            <EditOutlined />
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Container>
      <div className="page-header">
        <div>
          <h2>Хямдрал</h2>
          <p className="muted">
            Хямдралуудыг хайх, үүсгэх, өөрчлөх болон идэвхжүүлэх
          </p>
        </div>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleOpenCreate}>
          Хямдрал
        </Button>
      </div>

      <div className="filters">
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Код эсвэл нэрээр хайх"
          value={filters.search}
          onChange={(event) =>
            setFilters((prev) => ({ ...prev, search: event.target.value }))
          }
        />
        <Select
          value={filters.orderType}
          onChange={(value) => setFilters((prev) => ({ ...prev, orderType: value }))}
          options={[{ label: "Бүх төрөл", value: "all" }, ...orderTypeOptions]}
        />
        <Select
          value={filters.status}
          onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}
          options={[
            { label: "Бүх төлөв", value: "all" },
            { label: "Идэвхтэй", value: "active" },
            { label: "Идэвхгүй", value: "inactive" },
          ]}
        />
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={filteredDiscounts}
        loading={isLoading}
        pagination={false}
      />

      <DiscountFormModal
        title={editingDiscount ? "Хямдрал өөрчлөх" : "Хямдрал үүсгэх"}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingDiscount(null);
        }}
        onOk={handleSave}
        okText={editingDiscount ? "Хадгалах" : "Үүсгэх"}
        form={form}
      />
    </Container>
  );
};

const Container = styled.div`
  h2 {
    margin: 0;
  }

  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding: 18px 22px;
    border-radius: 8px;
    border: 1px solid rgba(22, 119, 255, 0.18);
    background: rgba(255, 255, 255, 0.94);
    box-shadow: 0 18px 50px rgba(33, 49, 63, 0.1);
    backdrop-filter: blur(8px);
  }

  .page-header h2 {
    font-size: 22px;
    color: #1f2a33;
  }

  .page-header p {
    color: #6b7a88;
  }

  .filters {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 12px;
    margin-bottom: 16px;
  }

  .muted {
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
  }
`;

export default Discount;
